import React, { useState } from 'react';
import { Check, ArrowRight, Sparkles, Building, Briefcase, Crown } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

type Cycle = 'monthly' | 'annual';

interface Tier {
  id: 'free' | 'pro' | 'agency' | 'agency_plus';
  name: string;
  tagline: string;
  monthly: number;
  annual: number;        // total per year
  branches: string;
  branding: string;
  cta: string;
  ctaHref: string;
  highlight?: boolean;
  icon: React.ReactNode;
  features: string[];
}

const TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Try it on your own site, no card needed.',
    monthly: 0,
    annual: 0,
    branches: '1',
    branding: 'Valmate-branded',
    cta: 'Sign up free',
    ctaHref: '/',
    icon: <Sparkles size={20} />,
    features: [
      'Unlimited valuations',
      'Unlimited leads',
      'Branded shareable URL (valmate.co.uk/v/your-name)',
      'Shows "Powered by Valmate" footer',
      'Lead capture + email notifications',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For solo agents and single-branch agencies.',
    monthly: 49,
    annual: 490,
    branches: '1',
    branding: 'White-label',
    cta: 'Upgrade to Pro',
    ctaHref: '/upgrade',
    highlight: true,
    icon: <Briefcase size={20} />,
    features: [
      'Everything in Free',
      'No Valmate branding anywhere',
      'White-label lead notification emails',
      'Custom primary brand colour + logo',
      'Priority email support',
    ],
  },
  {
    id: 'agency',
    name: 'Agency',
    tagline: 'Multi-branch agencies with up to 5 locations.',
    monthly: 149,
    annual: 1490,
    branches: 'up to 5',
    branding: 'White-label',
    cta: 'Choose Agency',
    ctaHref: '/upgrade?tier=agency',
    icon: <Building size={20} />,
    features: [
      'Everything in Pro',
      'Up to 5 branches / branded widgets',
      'Custom subdomain (yourname.valmate.co.uk) — coming soon',
      'Per-branch lead routing',
      'Multi-seat agent dashboard',
    ],
  },
  {
    id: 'agency_plus',
    name: 'Agency+',
    tagline: '5+ branches, custom integrations, account manager.',
    monthly: 299,
    annual: 2990,
    branches: '5+',
    branding: 'White-label',
    cta: 'Talk to us',
    ctaHref: 'mailto:hello@rehub.studio?subject=Valmate%20Agency%2B',
    icon: <Crown size={20} />,
    features: [
      'Everything in Agency',
      'Unlimited branches',
      'Public API access (read leads + valuations programmatically)',
      'Dedicated account manager',
      'Custom onboarding + agent training',
    ],
  },
];

export default function PricingPage() {
  const [cycle, setCycle] = useState<Cycle>('monthly');

  // Auth lives on the homepage — clicking Sign in / Get started from
  // the pricing page bounces home where the modal is wired up.
  const goHome = () => {
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      <Header onLogin={goHome} onSignUp={goHome} />

      <main className="flex-1 px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6 border"
              style={{
                background: 'rgba(244, 122, 107, 0.1)',
                borderColor: 'rgba(244, 122, 107, 0.25)',
                color: '#f9a397',
              }}
            >
              Simple pricing · all tiers unlimited
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Free forever. White-label from £49/mo.
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Every tier includes unlimited valuations and unlimited leads.
              Pay only when you want your own branding instead of ours.
            </p>
            <p className="text-xs text-slate-500 mt-3">
              Compare to Rightval (£59/mo) and ValPal (£200–£400/mo) — and we never
              gate the demo behind a sales call.
            </p>
          </div>

          {/* Cycle toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex p-1 rounded-full bg-slate-900/80 border border-slate-800">
              <CycleToggle current={cycle} value="monthly" label="Monthly" onChange={setCycle} />
              <CycleToggle
                current={cycle}
                value="annual"
                label="Annual"
                badge="Save 2 months"
                onChange={setCycle}
              />
            </div>
          </div>

          {/* Tier grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TIERS.map((tier) => (
              <TierCard key={tier.id} tier={tier} cycle={cycle} />
            ))}
          </div>

          {/* Doorsteps cross-sell strip */}
          <div className="mt-10 p-6 rounded-2xl bg-slate-900/40 border border-slate-800 text-center">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
              Already on Doorsteps Pro?
            </div>
            <div className="text-white text-base font-semibold">
              Valmate Pro is included in your Doorsteps Pro subscription — at no extra cost.
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Your widget auto-upgrades the moment you sign up — no extra payment, no extra setup.
            </div>
          </div>

          {/* FAQ-ish strip */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            <FactCard
              title="No usage caps. Ever."
              body="Whether you do 50 valuations a month or 5,000, the price is the same. Capping volume punishes the agents who promote the widget best."
            />
            <FactCard
              title="No demo required."
              body="Sign up, configure your widget, share the URL. Live in 5 minutes — no sales call, no contract minimums."
            />
            <FactCard
              title="Cancel any time."
              body="Cancellations end at the next billing date. Your widget never breaks — it just reverts to free with our branding back."
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function CycleToggle({
  current,
  value,
  label,
  badge,
  onChange,
}: {
  current: Cycle;
  value: Cycle;
  label: string;
  badge?: string;
  onChange: (v: Cycle) => void;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => onChange(value)}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
        active ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      {label}
      {badge && (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
          style={{
            background: active ? 'rgba(244, 122, 107, 0.25)' : 'rgba(244, 122, 107, 0.1)',
            color: '#f9a397',
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function TierCard({ tier, cycle }: { tier: Tier; cycle: Cycle }) {
  const price = cycle === 'monthly' ? tier.monthly : Math.round(tier.annual / 12);
  const isFree = tier.monthly === 0;
  const accent = '#f47a6b';

  return (
    <div
      className={`relative flex flex-col p-6 rounded-2xl border ${
        tier.highlight
          ? 'border-[#f47a6b]/40 bg-slate-900/80 shadow-xl shadow-[#f47a6b]/10'
          : 'border-slate-800 bg-slate-900/40'
      }`}
    >
      {tier.highlight && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white"
          style={{ background: accent }}
        >
          Most popular
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(244, 122, 107, 0.12)', color: accent }}
        >
          {tier.icon}
        </div>
        <div className="text-white font-bold text-lg">{tier.name}</div>
      </div>

      <p className="text-xs text-slate-400 mb-5 leading-relaxed">{tier.tagline}</p>

      <div className="mb-5">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-white tracking-tight">
            {isFree ? 'Free' : `£${price}`}
          </span>
          {!isFree && <span className="text-sm text-slate-500">/mo</span>}
        </div>
        {!isFree && cycle === 'annual' && (
          <div className="text-[11px] text-slate-500 mt-0.5">
            £{tier.annual} billed annually
          </div>
        )}
      </div>

      <div className="space-y-1.5 text-xs text-slate-400 mb-5">
        <Row label="Branches" value={tier.branches} />
        <Row label="Branding" value={tier.branding} />
        <Row label="Valuations" value="Unlimited" />
        <Row label="Leads" value="Unlimited" />
      </div>

      <ul className="space-y-2 mb-6 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-slate-300">
            <Check size={13} style={{ color: accent }} className="shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href={tier.ctaHref}
        className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
          tier.highlight
            ? 'text-white'
            : 'bg-slate-800 text-white hover:bg-slate-700'
        }`}
        style={tier.highlight ? { background: accent } : undefined}
      >
        {tier.cta}
        <ArrowRight size={13} />
      </a>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-300 font-medium">{value}</span>
    </div>
  );
}

function FactCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="p-5 rounded-2xl bg-slate-900/40 border border-slate-800">
      <h3 className="text-white font-semibold text-sm mb-2">{title}</h3>
      <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}
